/* Around Always — new header + menu behaviour
   - Desktop: dropdowns open on hover (and on click / keyboard), close on leave or Esc
   - Mobile: slide-in menu, accordion items (all closed by default)
   - Offer countdown, hides the offer when time is up (if the setting says so)
   Search uses Shopify's predictive search; cart and account keep using the theme's own code. */
(() => {
  if (window.__aaHeaderLoaded) return;
  window.__aaHeaderLoaded = true;

  /* ---------- Countdown ---------- */
  const pad = n => String(n).padStart(2, '0');
  function parseEnd(str) {
    if (!str) return NaN;
    // "2026-10-31 23:59 +0530" → ISO
    const m = String(str).trim().match(/^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*([+-]\d{2}):?(\d{2})?$/);
    if (m) return Date.parse(`${m[1]}T${pad(m[2])}:${m[3]}:${m[4] || '00'}${m[5]}:${m[6] || '00'}`);
    const t = Date.parse(str);
    return isNaN(t) ? Date.parse(String(str).replace(' ', 'T') + '+05:30') : t;
  }
  class AACountdown extends HTMLElement {
    connectedCallback() {
      this.end = parseEnd(this.dataset.end);
      if (isNaN(this.end)) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let s = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
      if (s === 0) {
        clearInterval(this.timer);
        this.textContent = '';
        const root = this.closest('[data-aa-header]');
        if (root && root.dataset.offerHideAfter === 'true') expireOffer(root);
        return;
      }
      const d = Math.floor(s / 86400); s %= 86400;
      const h = Math.floor(s / 3600); s %= 3600;
      const m = Math.floor(s / 60); s %= 60;
      const style = this.dataset.style;
      let html;
      if (style === 'pill') html = d > 0 ? `${d}d ${h}h` : `${pad(h)}:${pad(m)}:${pad(s)}`;
      else if (style === 'text') html = d > 0 ? `${d} day${d === 1 ? '' : 's'} left` : `${h}h ${pad(m)}m left`;
      else if (style === 'compact') html = `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;
      else html = [[d, 'DAYS'], [h, 'HRS'], [m, 'MIN'], [s, 'SEC']].map(([v, l]) => `<span class="aa-tb">${pad(v)}<small>${l}</small></span>`).join('');
      if (this._last !== html) { this.innerHTML = html; this._last = html; }
      this.setAttribute('aria-label', `${d} days ${h} hours ${m} minutes left`);
    }
  }
  if (!customElements.get('aa-countdown')) customElements.define('aa-countdown', AACountdown);

  function expireOffer(root) {
    root.querySelectorAll('[data-aa-offer]').forEach(el => { el.hidden = true; });
    root.classList.remove('aa-h--offer');
  }

  /* ---------- Header ---------- */
  function init(root) {
    if (root.__aa) return;
    root.__aa = true;
    const desktop = window.matchMedia('(min-width: 1025px)');

    /* Desktop dropdowns */
    const triggers = [...root.querySelectorAll('[data-aa-trigger]')];
    const bar = root.querySelector('.aa-bar');
    const host = root.querySelector('.aa-ddhost');
    let current = null, closeT = null;
    const ddOf = t => root.querySelector('#' + t.getAttribute('aria-controls'));
    function open(t) {
      clearTimeout(closeT);
      if (current === t) return;
      close(true);
      const dd = ddOf(t); if (!dd) return;
      dd.hidden = false; t.setAttribute('aria-expanded', 'true'); current = t;
    }
    function close(now) {
      clearTimeout(closeT);
      const run = () => {
        if (!current) return;
        const dd = ddOf(current); if (dd) dd.hidden = true;
        current.setAttribute('aria-expanded', 'false'); current = null;
      };
      now ? run() : (closeT = setTimeout(run, 160));
    }
    triggers.forEach(t => {
      t.addEventListener('mouseenter', () => desktop.matches && open(t));
      t.addEventListener('focus', () => { if (desktop.matches && t.matches(':focus-visible')) open(t); });
      t.addEventListener('click', e => {
        // first tap on touch laptops opens, second tap follows the link
        if (desktop.matches && current !== t) { e.preventDefault(); open(t); }
      });
    });
    root.querySelectorAll('[data-aa-plain]').forEach(a => a.addEventListener('mouseenter', () => close(true)));
    [bar, host].forEach(el => el && el.addEventListener('mouseleave', e => {
      if (!e.relatedTarget || !root.contains(e.relatedTarget) || e.relatedTarget.closest('.aa-drawer')) close();
    }));
    host && host.addEventListener('mouseenter', () => clearTimeout(closeT));
    root.querySelector('.aa-pills')?.addEventListener('mouseenter', () => close());
    root.querySelector('.aa-icons')?.addEventListener('mouseenter', () => close());
    root.addEventListener('focusout', e => { if (current && !root.contains(e.relatedTarget)) close(true); });

    /* Mobile drawer */
    const drawer = root.querySelector('[data-aa-drawer]');
    const burger = root.querySelector('[data-aa-open-drawer]');
    function openDrawer() {
      if (!drawer) return;
      root.__aaCloseSearch && root.__aaCloseSearch();
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      burger && burger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('aa-lock');
      drawer.querySelector('.aa-drawer__x')?.focus({ preventScroll: true });
    }
    function closeDrawer() {
      if (!drawer || drawer.hidden) return;
      drawer.classList.remove('is-open');
      burger && burger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('aa-lock');
      setTimeout(() => { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, 300);
      burger && burger.focus({ preventScroll: true });
    }
    burger && burger.addEventListener('click', openDrawer);
    root.querySelectorAll('[data-aa-close-drawer]').forEach(b => b.addEventListener('click', closeDrawer));
    desktop.addEventListener?.('change', e => { if (e.matches) closeDrawer(); else close(true); });

    /* Search panel (mobile and desktop) */
    const sBtn = root.querySelector('[data-aa-open-search]');
    const sBox = root.querySelector('[data-aa-search]');
    if (sBtn && sBox) {
      const input = sBox.querySelector('.aa-search__in');
      const res = sBox.querySelector('[data-aa-search-res]');
      let timer, ctrl;
      const esc = t => String(t).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
      const openSearch = () => { typeof close === 'function' && close(true); sBox.hidden = false; sBtn.setAttribute('aria-expanded', 'true'); setTimeout(() => input.focus(), 30); };
      const closeSearch = () => { if (sBox.hidden) return; sBox.hidden = true; sBtn.setAttribute('aria-expanded', 'false'); };
      sBtn.addEventListener('click', () => sBox.hidden ? openSearch() : closeSearch());
      sBox.querySelector('[data-aa-close-search]').addEventListener('click', () => { closeSearch(); sBtn.focus(); });
      document.addEventListener('click', e => { if (!sBox.hidden && !sBox.contains(e.target) && !sBtn.contains(e.target)) closeSearch(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') closeSearch(); });
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        if (q.length < 2) { res.innerHTML = ''; return; }
        timer = setTimeout(async () => {
          try {
            ctrl && ctrl.abort(); ctrl = new AbortController();
            const u = `${res.dataset.url}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
            const r = await fetch(u, { signal: ctrl.signal }); if (!r.ok) throw 0;
            const d = await r.json();
            const items = (d.resources && d.resources.results && d.resources.results.products) || [];
            const all = `<a class="aa-sr-all" href="${res.dataset.searchUrl}?type=product&q=${encodeURIComponent(q)}">${esc(res.dataset.allText)} &rarr;</a>`;
            res.innerHTML = items.length
              ? items.map(p => { const im = p.image || (p.featured_image && p.featured_image.url) || ""; return `<a class="aa-sr" href="${esc(p.url)}">${im ? `<img src="${esc(im)}${im.includes("?") ? "&" : "?"}width=120" alt="" loading="lazy">` : ""}<span>${esc(p.title)}</span></a>`; }).join('') + all
              : `<p class="aa-sr-none">${esc(res.dataset.noneText)}</p>` + all;
          } catch (e) { if (e && e.name === 'AbortError') return; res.innerHTML = ''; }
        }, 220);
      });
      root.__aaCloseSearch = closeSearch;
    }

    /* Accordion */
    const accs = [...root.querySelectorAll('[data-aa-acc]')];
    function setAcc(acc, on) {
      const b = acc.querySelector('.aa-acc__h'), body = acc.querySelector('.aa-acc__b');
      acc.classList.toggle('is-open', on); b.setAttribute('aria-expanded', on); body.hidden = !on;
    }
    accs.forEach(acc => acc.querySelector('.aa-acc__h').addEventListener('click', () => {
      const on = !acc.classList.contains('is-open');
      accs.forEach(a => a !== acc && setAcc(a, false));
      setAcc(acc, on);
    }));
    if (root.dataset.openFirst === 'true' && accs[0]) setAcc(accs[0], true);

    /* Esc closes everything */
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      if (current) { const t = current; close(true); t.focus(); }
      closeDrawer();
    });

    /* Theme editor: show things while they are being edited */
    if (window.Shopify && Shopify.designMode) {
      root.__aaOpenFirstDropdown = () => triggers[0] && open(triggers[0]);
      root.__aaOpenDrawer = openDrawer;
      root.__aaClose = () => { close(true); closeDrawer(); };
    }
  }

  const boot = () => document.querySelectorAll('[data-aa-header]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  document.addEventListener('shopify:section:load', e => {
    e.target.querySelectorAll('[data-aa-header]').forEach(init);
    document.documentElement.classList.remove('aa-lock');
  });
  document.addEventListener('shopify:block:select', e => {
    const root = e.target.closest('[data-aa-header]'); if (!root) return;
    if (window.matchMedia('(min-width: 1025px)').matches) root.__aaOpenFirstDropdown?.(); else root.__aaOpenDrawer?.();
  });
  document.addEventListener('shopify:block:deselect', e => {
    const root = e.target.closest('[data-aa-header]'); root && root.__aaClose?.();
  });
})();